import React from 'react'

import { Pill } from '@/components/pills/Pill'
import { Table } from '@/components/tables/Table/Table'

import { FEATURE_GATED_PERMISSIONS } from './validation'

import './FieldGuide.scss'

const REQUIRED_FIELDS = [
  {
    name: 'first_name',
    description: "User's first name. Required for new users.",
  },
  {
    name: 'last_name',
    description: "User's last name. Required for new users.",
  },
  {
    name: 'username',
    description: "User's email address. Acts as the unique identifier.",
  },
  {
    name: 'playbook',
    description:
      "Exact name of the playbook to assign. If we can't find a matching playbook the row will fail.",
  },
]

const OPTIONAL_FIELDS = [
  {
    name: 'voip_user_id',
    description:
      'VoIP identifier mapped to this agent. Required when granting complaints access. Leave the column out to keep the current value; a blank cell clears it.',
  },
  { name: 'external_system_id', description: 'External system identifier (e.g. CRM agent ID).' },
  {
    name: 'balto_cloud_access',
    description: 'yes / no — grants cloud login and sends a password-reset email.',
  },
  {
    name: 'playbook_edit_access',
    description: 'yes / no — lets the user edit playbooks.',
  },
  {
    name: 'user_management_access',
    description: 'yes / no — lets the user manage other users.',
  },
  { name: 'user_status', description: 'active / deactivated.' },
  {
    name: 'uncategorized_tags',
    description: 'Comma-separated tags. Lowercase, digits, dash, underscore only.',
  },
  {
    name: 'realtime_coaching_access',
    description: 'standard / admin — grants Realtime Coaching access.',
  },
  { name: 'edit_qa', description: 'yes / no' },
  { name: 'edit_leaderboard', description: 'yes / no' },
  { name: 'midcall_playbook_switching_enabled', description: 'yes / no' },
  { name: 'desktop_enterprise_enabled', description: 'yes / no' },
  { name: 'edit_qa_copilot', description: 'yes / no' },
  { name: 'hierarchy_manager', description: 'yes / no — hierarchy manager permission.' },
  {
    name: 'screen_capture_enabled',
    description:
      'yes / no / null — requires Screen Capture Access on the organization. null defers to the org default.',
  },
  { name: 'agent_gpt_access', description: 'yes / no' },
  { name: 'tools_editor', description: 'yes / no' },
  {
    name: 'outbound_campaign_editor',
    description: 'yes / no — lets the user edit outbound campaigns.',
  },
  {
    name: 'togo_editor',
    description: 'yes / no — lets the user edit Togo.',
  },
  {
    name: 'desired_username',
    description: "Use to rename an existing user's email/username.",
  },
]

// Returns the org feature flag name that gates this field, or null if the
// field isn't feature-gated.
const featureFlagFor = (fieldName) => FEATURE_GATED_PERMISSIONS[fieldName]?.flag ?? null

export const FieldGuide = ({ tagCategories = [], organizationFeatureFlags = {} }) => {
  const tagCategoryNames = tagCategories.map((category) => category.name)

  const isDisabled = (fieldName) => {
    const flag = featureFlagFor(fieldName)
    return flag !== null && !organizationFeatureFlags[flag]
  }

  const renderRow = (field, statusPill) => {
    const disabled = isDisabled(field.name)
    return (
      <tr key={field.name} className={disabled ? 'field-guide__row--disabled' : undefined}>
        <td>
          <code>{field.name}</code>
        </td>
        <td>{disabled ? <Pill xs>Disabled for org</Pill> : statusPill}</td>
        <td>{field.description}</td>
      </tr>
    )
  }

  return (
    <div className="field-guide" data-testid="field-guide">
      <Table compact className="field-guide__table">
        <thead>
          <tr>
            <th>Field</th>
            <th>Required</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {REQUIRED_FIELDS.map((field) =>
            renderRow(
              field,
              <Pill critical xs>
                Required
              </Pill>
            )
          )}
          {OPTIONAL_FIELDS.map((field) =>
            renderRow(
              field,
              <Pill informative xs>
                Optional
              </Pill>
            )
          )}
          {tagCategoryNames.map((categoryName) => (
            <tr key={categoryName}>
              <td>
                <code>{categoryName}</code>
              </td>
              <td>
                <Pill informative xs>
                  Optional
                </Pill>
              </td>
              <td>{`Tag from the "${categoryName}" category.`}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  )
}
